import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import { GCHAT_ENABLED } from "@/lib/featureFlags";

// Iter 91f — Code-split the heaviest routes so the initial JS bundle
// drops by ~60%. Triage (the landing route at "/") still loads eagerly
// to avoid a spinner on the most common first page. Everything else
// loads on demand. Each lazy chunk caches in the browser so subsequent
// navigations are instant.
import Triage from "@/pages/Triage";
const Today = lazy(() => import("@/pages/Today"));
const Clients = lazy(() => import("@/pages/Clients"));
const ClientDetail = lazy(() => import("@/pages/ClientDetail"));
const ClosePeriodDetail = lazy(() => import("@/pages/ClosePeriodDetail"));
const Templates = lazy(() => import("@/pages/Templates"));
const Periods = lazy(() => import("@/pages/Periods"));
const Team = lazy(() => import("@/pages/Team"));
const Timesheet = lazy(() => import("@/pages/Timesheet"));
const TimeReports = lazy(() => import("@/pages/TimeReports"));
const RotaVariancePage = lazy(() => import("@/pages/RotaVariancePage"));
const AmlDashboard = lazy(() => import("@/pages/AmlDashboard"));
// Iter 110y — GChat kill-switch (see /app/frontend/src/lib/featureFlags.js).
// The lazy import is kept so re-enabling is a one-line change.
const GChat = lazy(() => import("@/pages/GChat"));
const Board = lazy(() => import("@/pages/Board"));
const VatCalendar = lazy(() => import("@/pages/VatCalendar"));
const StatutoryFilings = lazy(() => import("@/pages/StatutoryFilings"));
const AuditLog = lazy(() => import("@/pages/AuditLog"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const Profile = lazy(() => import("@/pages/Profile"));
const FirmDrive = lazy(() => import("@/pages/FirmDrive"));
import { ComposeProvider } from "@/contexts/ComposeContext";
import FloatingComposeRoot from "@/components/FloatingComposeRoot";

// Suspense fallback — intentionally minimal so it's invisible on fast networks.
const PageLoader = () => (
  <div className="flex items-center justify-center h-screen text-sm text-slate-400">
    Loading…
  </div>
);

function ManagerOnly({ children }) {
  const { user } = useAuth();
  if (user && !["admin", "manager"].includes(user.role)) return <Navigate to="/timesheet" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

// Iter 142 — Route guard driven by the `effective_permissions` list
// surfaced on `/auth/me`. Falls back to deny-by-default if the field
// isn't present (older sessions mid-rollout, or an unauthed user).
function PermissionOnly({ perm, redirect = "/", children }) {
  const { user } = useAuth();
  if (!user) return null;
  const allowed = (user.effective_permissions || []).includes(perm);
  if (!allowed) return <Navigate to={redirect} replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Synchronously detect OAuth return; prevents race vs ProtectedRoute / AuthProvider
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
        {/* Iter 113 — Root is now the Today dashboard. Triage moves to
            /triage; both are top-level nav entries in the sidebar. */}
        <Route path="/" element={<Today />} />
        <Route path="/triage" element={<Triage />} />
        <Route path="/inbox" element={<Navigate to="/triage" replace />} />
        <Route path="/firm-drive" element={<FirmDrive />} />
        <Route path="/clients" element={<Clients />} />
        <Route path="/clients/:clientId" element={<ClientDetail />} />
        <Route path="/periods" element={<Periods />} />
        <Route path="/vat" element={<VatCalendar />} />
        <Route path="/filings" element={<StatutoryFilings />} />
        <Route path="/audit" element={<AuditLog />} />
        <Route path="/periods/:periodId" element={<ClosePeriodDetail />} />
        <Route path="/templates" element={<Templates />} />
        <Route path="/timesheet" element={<Timesheet />} />
        <Route path="/time-reports" element={<PermissionOnly perm="view_time_reports"><TimeReports /></PermissionOnly>} />
        <Route path="/rota" element={<PermissionOnly perm="view_rota_variance"><RotaVariancePage /></PermissionOnly>} />
        {/* Iter 110y — /chat mounted only when GCHAT_ENABLED. Direct URL
            access falls through to NotFound while the module is disabled. */}
        {GCHAT_ENABLED && <Route path="/chat" element={<GChat />} />}
        <Route path="/board" element={<Board />} />
        <Route path="/team" element={<AdminOnly><Team /></AdminOnly>} />
        <Route path="/admin" element={<AdminOnly><AdminDashboard /></AdminOnly>} />
        <Route path="/admin/aml-dashboard" element={<AdminOnly><AmlDashboard /></AdminOnly>} />
        <Route path="/profile" element={<Profile />} />
      </Route>
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
          <AuthProvider>
            <ComposeProvider>
              <AppRouter />
              <FloatingComposeRoot />
              <Toaster richColors position="top-right" />
            </ComposeProvider>
          </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
